/**
 * The pos-service client of the register (T07-22): the generated `openapi-fetch` client
 * (`@tcg/api-client` → `createPosClient`) with the POS conventions on every request:
 *
 * | header | value |
 * |---|---|
 * | `Authorization` | the staff token (PIN) when signed in, else the device token (`auth.ts`) |
 * | `X-Register-Id` | the register this tablet is paired with (pos-service checks it = token's) |
 * | `Idempotency-Key` | a UUID v7 on every POST/PUT/PATCH/DELETE sent without one (a screen that can retry — お会計 — sets its own and keeps it) |
 * | `Accept-Language` | the staff member's language (errors come back translated) |
 *
 * A 401 goes to the auth controller (revoked tablet → pairing screen; expired staff token → PIN).
 * Use `call()` to get the data or an `ApiError` (never a raw response).
 */
import {
  createConsignmentClient,
  createMediaClient,
  createOrdersClient,
  createPosClient,
  createTaxClient,
  type ConsignmentClient,
  type MediaClient,
  type OrdersClient,
  type PosClient,
  type TaxClient,
} from "@tcg/api-client";
import type { Middleware } from "openapi-fetch";

import type { AuthController } from "./auth";
import { apiOrigin } from "./env";
import { ApiError, unwrap } from "./errors";
import { isReadOnlyPost, MUTATING_METHODS, newIdempotencyKey } from "./idempotency";

export interface PosApiOptions {
  auth: Pick<AuthController, "bearer" | "handleUnauthorized" | "getSnapshot">;
  getLocale: () => string;
  baseUrl?: string;
  fetch?: typeof fetch;
}

export function registerIdOf(auth: Pick<AuthController, "getSnapshot">): string | null {
  const state = auth.getSnapshot();
  return state.kind === "locked" || state.kind === "unlocked" ? state.device.registerId : null;
}

export function posMiddleware(options: PosApiOptions): Middleware {
  return {
    async onRequest({ request }) {
      const registerId = registerIdOf(options.auth);
      if (registerId && !request.headers.has("X-Register-Id")) {
        request.headers.set("X-Register-Id", registerId);
      }
      const path = new URL(request.url, "http://pos.invalid").pathname;
      if (
        MUTATING_METHODS.has(request.method) &&
        !request.headers.has("Idempotency-Key") &&
        !isReadOnlyPost(path)
      ) {
        request.headers.set("Idempotency-Key", newIdempotencyKey());
      }
      if (!request.headers.has("Authorization")) {
        const token = await options.auth.bearer();
        if (token) {
          request.headers.set("Authorization", `Bearer ${token}`);
        }
      }
      return request;
    },
    async onResponse({ response }) {
      if (response.status === 401) {
        const body: unknown = await response
          .clone()
          .json()
          .catch(() => null);
        await options.auth.handleUnauthorized(ApiError.fromEnvelope(401, body));
      }
      return response;
    },
  };
}

export function createPosApi(options: PosApiOptions): PosClient {
  const client = createPosClient({
    baseUrl: options.baseUrl ?? apiOrigin(),
    getLocale: options.getLocale,
    ...(options.fetch ? { fetch: options.fetch } : {}),
  });
  client.use(posMiddleware(options));
  return client;
}

/**
 * tax-service's register routes (`/v1/pos/tax-free/*` — the gateway sends them there, T07-13):
 * the 免税 records list of the shift (T07-30) — same headers as pos-service.
 */
export function createTaxApi(options: PosApiOptions): TaxClient {
  const client = createTaxClient({
    baseUrl: options.baseUrl ?? apiOrigin(),
    getLocale: options.getLocale,
    ...(options.fetch ? { fetch: options.fetch } : {}),
  });
  client.use(posMiddleware(options));
  return client;
}

/**
 * consignment-service's register routes (the gateway sends them there — row 48): 本人確認
 * (`/pos/identity-checks`), 古物台帳 (`/pos/kobutsu-ledger`), 委託受付 (`/pos/consignment-intakes`,
 * `/pos/consignors`) — T07-31 / T07-32; plus the few admin routes a manager uses at the counter
 * (a new consignor, the agreement signed on paper, an intake's documents).
 */
export function createConsignmentApi(options: PosApiOptions): ConsignmentClient {
  const client = createConsignmentClient({
    baseUrl: options.baseUrl ?? apiOrigin(),
    getLocale: options.getLocale,
    ...(options.fetch ? { fetch: options.fetch } : {}),
  });
  client.use(posMiddleware(options));
  return client;
}

/**
 * media-service's register routes: the ID photo of 本人確認 goes **straight to media-service's
 * encrypted upload** (`/pos/media/restricted` — never through pos-service, T07-31), card photos
 * through the presigned upload (`/pos/media/uploads`), documents (委託契約書 / 預り証 PDF).
 */
export function createMediaApi(options: PosApiOptions): MediaClient {
  const client = createMediaClient({
    baseUrl: options.baseUrl ?? apiOrigin(),
    getLocale: options.getLocale,
    ...(options.fetch ? { fetch: options.fetch } : {}),
  });
  client.use(posMiddleware(options));
  return client;
}

/** order-service's public 郵便番号 → 住所 lookup (the address of the 本人確認 form). */
export function createOrdersApi(options: PosApiOptions): OrdersClient {
  const client = createOrdersClient({
    baseUrl: options.baseUrl ?? apiOrigin(),
    getLocale: options.getLocale,
    ...(options.fetch ? { fetch: options.fetch } : {}),
  });
  client.use(posMiddleware(options));
  return client;
}

/** Run a generated-client call → its data, or throw `ApiError` (network failure = `network`). */
export async function call<T>(
  request: () => Promise<{ data?: T; error?: unknown; response: Response }>,
): Promise<T> {
  let result: { data?: T; error?: unknown; response: Response };
  try {
    result = await request();
  } catch (cause) {
    throw ApiError.network(cause);
  }
  return unwrap(result);
}

export type { ConsignmentClient, MediaClient, OrdersClient, PosClient, TaxClient };
